import { supportsBarcodeDetector } from './platform';

export interface DecodedBarcode {
  symbology: string;
  value: string;
}

/** Which decoder reads a frame on this device. */
export type BarcodeDecoderKind = 'native' | 'zxing';

/** RGBA pixels, the shape `CanvasRenderingContext2D.getImageData` returns. */
export interface ImagePixels {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

const NATIVE_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'qr_code', 'data_matrix'];

/**
 * The native `BarcodeDetector` where the browser has one (Chrome, Android
 * WebView), otherwise ZXing in JavaScript. iOS Safari has no
 * `BarcodeDetector`, and before this fallback every iPhone reported
 * "Barcode capture falls back to manual entry" and read no barcode at all
 * (CAPTURE_JOBS.md section 6).
 */
export function selectBarcodeDecoder(): BarcodeDecoderKind {
  return supportsBarcodeDetector() ? 'native' : 'zxing';
}

/**
 * On-device barcode decode. Returns `null` rather than throwing when
 * nothing is found or the decoder fails, so a frame without a readable
 * barcode still becomes a capture.
 *
 * A native detector that throws falls through to ZXing: desktop Chrome on
 * some platforms defines `BarcodeDetector` and then refuses every call.
 */
export async function decodeBarcodeFromImage(source: ImageBitmapSource): Promise<DecodedBarcode | null> {
  if (selectBarcodeDecoder() === 'native') {
    try {
      return await decodeNative(source);
    } catch {
      // Fall through to the JavaScript decoder below.
    }
  }
  const pixels = readPixels(source);
  if (!pixels) return null;
  try {
    return await decodePixels(pixels);
  } catch {
    return null;
  }
}

async function decodeNative(source: ImageBitmapSource): Promise<DecodedBarcode | null> {
  const Detector = (window as unknown as { BarcodeDetector: new (opts?: { formats?: string[] }) => BarcodeDetectorLike })
    .BarcodeDetector;
  const detector = new Detector({ formats: NATIVE_FORMATS });
  const results = await detector.detect(source);
  if (results.length === 0) return null;
  const [first] = results;
  return { symbology: first.format, value: first.rawValue };
}

interface BarcodeDetectorLike {
  detect(source: ImageBitmapSource): Promise<{ format: string; rawValue: string }[]>;
}

/** Draws the frame onto a canvas and reads its pixels back, or `null` where
 *  there is no 2D canvas to draw on. */
function readPixels(source: ImageBitmapSource): ImagePixels | null {
  const sized = source as { width?: number; height?: number; videoWidth?: number; videoHeight?: number };
  const width = sized.videoWidth || sized.width || 0;
  const height = sized.videoHeight || sized.height || 0;
  if (!width || !height || typeof document === 'undefined') return null;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  try {
    ctx.drawImage(source as CanvasImageSource, 0, 0, width, height);
    const image = ctx.getImageData(0, 0, width, height);
    return { data: image.data, width: image.width, height: image.height };
  } catch {
    return null;
  }
}

/** Rec. 601 luma, one byte per pixel, which is what ZXing's luminance
 *  source reads when handed a `Uint8ClampedArray`. */
export function toLuminance(pixels: ImagePixels): Uint8ClampedArray {
  const { data, width, height } = pixels;
  const luminance = new Uint8ClampedArray(width * height);
  for (let i = 0, p = 0; i < luminance.length; i += 1, p += 4) {
    luminance[i] = (data[p] * 299 + data[p + 1] * 587 + data[p + 2] * 114) / 1000;
  }
  return luminance;
}

/**
 * Decodes RGBA pixels with ZXing. The library is imported on first use, so
 * a device with a native detector never downloads it.
 */
export async function decodePixels(pixels: ImagePixels): Promise<DecodedBarcode | null> {
  const zxing = await import('@zxing/library');
  const source = new zxing.RGBLuminanceSource(toLuminance(pixels), pixels.width, pixels.height);
  const bitmap = new zxing.BinaryBitmap(new zxing.HybridBinarizer(source));
  const hints = new Map<import('@zxing/library').DecodeHintType, unknown>([[zxing.DecodeHintType.TRY_HARDER, true]]);
  const reader = new zxing.MultiFormatReader();
  try {
    const result = reader.decode(bitmap, hints);
    return {
      // ZXing names formats in upper case (`EAN_13`); the native detector
      // and the capture record use the lower-case form (`ean_13`).
      symbology: String(zxing.BarcodeFormat[result.getBarcodeFormat()]).toLowerCase(),
      value: result.getText(),
    };
  } catch (cause) {
    if (cause instanceof zxing.NotFoundException) return null;
    throw cause;
  }
}
